import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Playback of the take just recorded (an object URL), for the review's
 * «صدای من» card: where it is and whether it is playing. The element is
 * made in an effect, not during render, so the component also renders
 * where there is no `Audio` at all.
 */
export function useTakePlayer(url) {
  const audioRef = useRef(null)
  const [playing, setPlaying] = useState(false)
  const [position, setPosition] = useState(0)

  useEffect(() => {
    if (!url || typeof Audio === 'undefined') return undefined
    const audio = new Audio(url)
    audio.preload = 'metadata'
    const onPlay = () => setPlaying(true)
    const onPause = () => setPlaying(false)
    const onEnded = () => {
      setPlaying(false)
      setPosition(0)
    }
    const onTime = () => setPosition(audio.currentTime)
    audio.addEventListener('play', onPlay)
    audio.addEventListener('pause', onPause)
    audio.addEventListener('ended', onEnded)
    audio.addEventListener('timeupdate', onTime)
    audioRef.current = audio
    setPlaying(false)
    setPosition(0)
    return () => {
      audio.pause()
      audio.removeEventListener('play', onPlay)
      audio.removeEventListener('pause', onPause)
      audio.removeEventListener('ended', onEnded)
      audio.removeEventListener('timeupdate', onTime)
      audioRef.current = null
    }
  }, [url])

  const pause = useCallback(() => {
    audioRef.current?.pause()
  }, [])

  const toggle = useCallback(async () => {
    const audio = audioRef.current
    if (!audio) return
    if (audio.paused) await audio.play().catch(() => setPlaying(false))
    else audio.pause()
  }, [])

  return { playing, position, toggle, pause }
}
